import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { portalApi, portalError } from "./portalApi";
import { Btn, Input, Label } from "../components/UI";
import { QRCodeSVG } from "qrcode.react";
import { Home, CalendarDays, Pill, FileSignature, ListChecks, UserCircle2, LogOut, Bell, ShieldCheck, Copy, MessageSquareHeart, GraduationCap, ClipboardCheck, Mail, Store } from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { to: "/portal", label: "Home", icon: Home, end: true },
  { to: "/portal/onboarding", label: "Onboarding", icon: ClipboardCheck },
  { to: "/portal/appointments", label: "Appointments", icon: CalendarDays },
  { to: "/portal/medications", label: "Medications", icon: Pill },
  { to: "/portal/pharmacies", label: "Pharmacies", icon: Store },
  { to: "/portal/messages", label: "Messages", icon: Mail },
  { to: "/portal/documents", label: "Documents", icon: FileSignature },
  { to: "/portal/education", label: "Education", icon: GraduationCap },
  { to: "/portal/feedback", label: "Feedback", icon: MessageSquareHeart },
  { to: "/portal/tasks", label: "Tasks & Reminders", icon: ListChecks },
  { to: "/portal/profile", label: "My Profile", icon: UserCircle2 },
];

function TwoFactorSetup({ onDone }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    portalApi.post("/portal/auth/2fa/setup").then((r) => setSetup(r.data)).catch((e) => toast.error(portalError(e)));
  }, []);
  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post("/portal/auth/2fa/enable", { code });
      toast.success("Two-factor authentication enabled - your account is now protected");
      onDone();
    } catch (err) { toast.error(portalError(err)); }
    setBusy(false);
  };
  return (
    <div className="mx-auto max-w-md rounded-2xl bg-white p-7 shadow-lg" data-testid="portal-force-2fa">
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-[#3E2723]"><ShieldCheck size={19} className="text-[#2E7D32]" /> Step 2 of 2 - Secure your account</h2>
      <p className="mt-1 mb-4 text-sm text-[#7B6157]">Your clinic requires two-factor authentication. Scan this QR code with any authenticator app (Google Authenticator, Authy, 1Password…), then enter the 6-digit code.</p>
      {!setup ? <div className="py-8 text-center text-sm text-[#84675B]">Generating your secure key…</div> : (
        <form onSubmit={enable} className="space-y-4">
          <div className="flex justify-center rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-5" data-testid="portal-2fa-qr">
            <QRCodeSVG value={setup.uri} size={168} bgColor="transparent" fgColor="#3E2723" />
          </div>
          <div className="flex items-center justify-center gap-2 text-[11px] text-[#7B6157]">
            Can't scan? Key: <code className="font-mono font-bold text-[#3E2723]" data-testid="portal-2fa-secret">{setup.secret}</code>
            <button type="button" onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Copied"); }} className="p-1 rounded hover:bg-[#F5F2EB]"><Copy size={12} /></button>
          </div>
          <div><Label>6-digit code from your app</Label>
            <Input data-testid="portal-2fa-code-input" inputMode="numeric" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" required className="text-center tracking-[0.4em] font-bold text-lg" /></div>
          <Btn data-testid="portal-2fa-enable-btn" type="submit" disabled={busy || code.length !== 6} className="w-full">{busy ? "Verifying…" : "Enable & Enter Portal"}</Btn>
        </form>
      )}
    </div>
  );
}

export default function PortalLayout() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);

  const loadMe = () => portalApi.get("/portal/me").then((r) => setMe(r.data))
    .catch(() => { localStorage.removeItem("cocoa_portal_token"); navigate("/login"); });
  useEffect(() => { document.title = "Client Portal | COCOA EHR"; loadMe(); }, []); // eslint-disable-line

  const logout = () => { localStorage.removeItem("cocoa_portal_token"); navigate("/login"); };

  const changePw = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post("/portal/auth/change-password", { new_password: newPw });
      toast.success("Password updated - welcome to your portal!");
      loadMe();
    } catch (err) { toast.error(portalError(err)); }
    setBusy(false);
  };

  if (!me) return <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] text-[#84675B]">Loading your portal…</div>;

  return (
    <div className="min-h-screen bg-[#F5F2EB]">
      <header className="bg-[#3E2723] text-white">
        <div className="mx-auto max-w-5xl px-4 py-3 flex items-center gap-3">
          <img src="/logos/cocoa_ehr_logo_white.webp" alt="Cocoa EHR" className="h-9 w-auto" />
          <div className="hidden sm:block">
            <div className="text-xs text-white/60">Client Portal</div>
            <div className="text-sm font-bold" data-testid="portal-clinic-name">{me.clinic?.name}</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold" data-testid="portal-client-name">{me.client.preferred_name || me.client.first_name} {me.client.last_name}</div>
              <div className="text-[10px] text-white/60">HRN {me.client.health_record_number || "-"}{me.clinician ? ` · ${me.clinician}` : ""}</div>
            </div>
            <div className="h-9 w-9 rounded-full bg-[#8D6E63] flex items-center justify-center font-bold text-sm overflow-hidden">
              {me.client.photo ? <img src={me.client.photo} alt="" className="h-9 w-9 object-cover" /> : <>{me.client.first_name?.[0]}{me.client.last_name?.[0]}</>}
            </div>
            <button data-testid="portal-logout-btn" onClick={logout} title="Sign out" className="p-2 rounded-full hover:bg-white/10"><LogOut size={17} /></button>
          </div>
        </div>
        <nav className="mx-auto max-w-5xl px-4 flex gap-1 overflow-x-auto">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`portal-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-1.5 whitespace-nowrap px-3.5 py-2.5 text-[13px] font-semibold border-b-2 transition-colors ${isActive ? "border-[#FFB300] text-white" : "border-transparent text-white/60 hover:text-white"}`}>
              <Icon size={14} /> {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">
        {me.must_change_password ? (
          <div className="mx-auto max-w-md rounded-2xl bg-white p-7 shadow-lg" data-testid="portal-force-password">
            <h2 className="font-heading text-lg font-bold text-[#3E2723]">Step 1 of 2 - Set your new password</h2>
            <p className="mt-1 mb-4 text-sm text-[#7B6157]">For your security, please replace the temporary password your clinic gave you.</p>
            <form onSubmit={changePw} className="space-y-4">
              <div><Label>New password (min 8 characters)</Label>
                <Input data-testid="portal-newpw-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={8} required /></div>
              <Btn data-testid="portal-newpw-submit" type="submit" disabled={busy} className="w-full">{busy ? "Saving…" : "Set Password & Continue"}</Btn>
            </form>
          </div>
        ) : me.must_setup_2fa ? (
          <TwoFactorSetup onDone={loadMe} />
        ) : (
          <Outlet context={{ me, refreshMe: loadMe }} />
        )}
      </main>
      <footer className="pb-6 text-center text-[11px] text-[#84675B]">
        <Bell size={11} className="inline mr-1 -mt-0.5" />HIPAA-secure client portal · Your activity is protected & audit-logged
      </footer>
    </div>
  );
}
